<script setup lang="ts">
import { computed } from 'vue';
import { TVulnerabilityCodeLine, TVulnerabilityDetails } from '../../models/types';

const props = defineProps<{
  vulnerability: TVulnerabilityDetails;
  block: {
    file: string;
    lines: Array<{ line_number: number; content: string; is_matched: boolean }>;
    minSpaces: number;
    source_type: string;
  };
}>();

const MIN_SPACES_BEFORE_CODE = '   ';

const severity = computed(() => props.vulnerability.category.severity || null);

function getSeverityLineClass(sev: string | undefined): string {
  if (sev === 'low') return 'tw:bg-vuln-low';
  if (sev === 'medium') return 'tw:bg-vuln-medium';
  if (sev === 'high') return 'tw:bg-vuln-high';

  return 'tw:bg-[lightgrey]';
}

function getHighlightedLineClass(line: TVulnerabilityCodeLine): string {
  return line.is_matched ? 'tw:bg-[#feeae7]' : '';
}

function getHighlightLanguageBySourceType(sourceType: string): string {
  return ['xml', 'java', 'swift'].find((lang) => lang === sourceType) ?? 'plaintext';
}
</script>

<template>
  <div class="tw:w-full">
    <div class="tw:flex">
      <div class="tw:typography-small tw:text-secondary tw:tracking-[0.02em] tw:w-full">
        Found in file
        <span class="tw:typography-h3 tw:text-main tw:wrap-break-word">{{ block.file }}</span>
      </div>
    </div>

    <div
      class="code-block tw:p-0! tw:mt-4 tw:flex tw:overflow-auto tw:relative tw:[&::-webkit-scrollbar]:hidden"
    >
      <div
        v-if="block.lines.length > 0"
        class="tw:block tw:border-2 tw:border-transparent"
        :class="getSeverityLineClass(severity)"
      />
      <table
        class="tw:w-full tw:bg-general tw:border-collapse tw:border-0 tw:overflow-hidden tw:transition-all tw:duration-300"
      >
        <tbody>
          <tr
            v-for="(line, lineIndex) in block.lines"
            :key="lineIndex"
            :class="getHighlightedLineClass(line)"
          >
            <td
              class="tw:p-0 tw:pl-10 tw:pr-3.75 tw:w-5 tw:tabular-nums tw:text-[14px] tw:leading-6.5 code-font"
            >
              {{ line.line_number }}
            </td>
            <td class="tw:p-0">
              <pre
                v-highlightjs="MIN_SPACES_BEFORE_CODE + line.content.substring(block.minSpaces)"
                class="tw:m-0! tw:text-sm! tw:leading-6! tw:bg-transparent! tw:border-none tw:h-full tw:text-main"
              ><code class="tw:p-0! tw:text-main! tw:text-sm! tw:leading-6! tw:text-shadow-none!" :class="getHighlightLanguageBySourceType(block.source_type)"></code></pre>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// TODO: replace with a proper local font import
@import url('https://fonts.googleapis.com/css2?family=Inconsolata&display=swap');

.code-block,
.code-font {
  font-family: 'Inconsolata', monospace;
}
</style>
